import { z } from 'zod';

// The worker protocol (design §6.6, plan 1B). Every request carries
// `Authorization: Bearer $ITC_WORKER_TOKEN` and is served only under /internal,
// which Caddy answers with 404.
//
// 1. POST /internal/worker/hello with WorkerHello → WorkerWelcome (a session id).
// 2. GET /internal/worker/stream?session=… (server-sent events): each `data:` line
//    is a StreamMessage. Pending commands for the session's capabilities come
//    first, oldest first, then new ones as they commit, and a ping every 15 s.
// 3. POST /internal/worker/commands/:id with a CommandReport: `claimed` (compare-
//    and-set, so one worker wins), then `done` with the result or `failed`.
// 4. POST /internal/worker/events with an EventBatch: forwarded Discord events,
//    published as `discord.*` domain events in one transaction, plus notices.

/** The header a worker names itself with on every request. */
export const WORKER_ID_HEADER = 'x-itc-worker';

export const WorkerId = z.string().regex(/^[A-Za-z0-9_.:-]{1,64}$/);

export const WorkerHello = z.object({
  workerId: WorkerId,
  /** The worker's release, for the system page. */
  version: z.string().min(1).max(64),
  /** The capability names this worker can run. */
  capabilities: z.array(z.string().min(1).max(64)).max(500),
});
export type WorkerHello = z.infer<typeof WorkerHello>;

export const WorkerWelcome = z.object({
  session: z.string(),
  /** How often the stream pings. A worker that hears nothing for 3 pings should reconnect. */
  pingMs: z.number().int(),
  /** Announced capabilities the server doesn't know; it never sends commands for them. */
  ignored: z.array(z.string()),
});
export type WorkerWelcome = z.infer<typeof WorkerWelcome>;

export const CommandStatus = z.enum(['pending', 'claimed', 'done', 'failed', 'expired', 'unknown']);
export type CommandStatus = z.infer<typeof CommandStatus>;

/** A command as pushed to a worker. */
export const PushedCommand = z.object({
  id: z.string(),
  capability: z.string(),
  /** The module that staged it, or `core`. */
  module: z.string(),
  payload: z.unknown(),
  deadline: z.iso.datetime(),
  /** 1 on first delivery; more after a retry. */
  attempt: z.number().int().min(1),
  /** Who it's done for, for Discord's audit log: `user:<id>`, `discord:<discord id>` or `service:<module>`, and a readable name. */
  actor: z.object({ ref: z.string(), name: z.string() }),
});
export type PushedCommand = z.infer<typeof PushedCommand>;

export const StreamMessage = z.discriminatedUnion('type', [
  z.object({ type: z.literal('command'), command: PushedCommand }),
  z.object({ type: z.literal('ping'), at: z.iso.datetime() }),
]);
export type StreamMessage = z.infer<typeof StreamMessage>;

export const CommandError = z.object({
  message: z.string().max(4000),
  /** Discord's error code, such as 50013 (Missing Permissions), or a short name. */
  code: z.string().max(64).optional(),
});
export type CommandError = z.infer<typeof CommandError>;

export const CommandReport = z.discriminatedUnion('status', [
  z.object({ status: z.literal('claimed'), session: z.string() }),
  z.object({ status: z.literal('done'), session: z.string(), result: z.unknown() }),
  z.object({ status: z.literal('failed'), session: z.string(), error: CommandError }),
]);
export type CommandReport = z.infer<typeof CommandReport>;

export const CommandReportAnswer = z.object({ status: CommandStatus, attempt: z.number().int() });
export type CommandReportAnswer = z.infer<typeof CommandReportAnswer>;

/** One forwarded Discord event. `id` lets the server drop a batch it already took when a retry repeats it. */
export const ForwardedEvent = z.object({
  id: z.string().min(1).max(64),
  type: z.string().min(1).max(128),
  occurredAt: z.iso.datetime(),
  data: z.unknown(),
});
export type ForwardedEvent = z.infer<typeof ForwardedEvent>;

/**
 * `gateway_ready`: the worker started a new gateway session (a re-identify, which
 * loses events). `buffer_overflowed`: it dropped buffered items while the server was
 * unreachable. Both ask the server for a reconcile.
 */
export const WorkerNotice = z.enum(['gateway_ready', 'buffer_overflowed']);
export type WorkerNotice = z.infer<typeof WorkerNotice>;

export const EventBatch = z.object({
  events: z.array(ForwardedEvent).max(50),
  notices: z.array(WorkerNotice).max(10).default([]),
});
export type EventBatch = z.input<typeof EventBatch>;

/**
 * POST /internal/worker/invoke: run a module's endpoint as the Discord user who
 * used a slash command, button or modal. `request.path` is under /api, starting
 * with `/<module>`; the server checks the interaction's permission, then runs the
 * request through the same pipeline as the browser's. `displayName` is what the
 * guild calls them, for an interaction declaring `discord`, whose user may have no
 * ITC Web record to read it from.
 */
export const InvokeRequest = z.object({
  module: z.string().regex(/^[a-z][a-z0-9]*$/),
  interaction: z.object({ kind: z.enum(['command', 'button', 'modal']), name: z.string().min(1).max(100), id: z.string().regex(/^[0-9]{1,32}$/) }),
  actingUser: z.object({ discordId: z.string().regex(/^[0-9]{1,32}$/), displayName: z.string().max(128) }),
  request: z.object({
    method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
    path: z.string().regex(/^\/[A-Za-z0-9/_.~%-]*$/, 'a path without a query string').max(512),
    query: z.record(z.string(), z.string()).optional(),
    body: z.unknown().optional(),
  }),
});
export type InvokeRequest = z.input<typeof InvokeRequest>;

export const InvokeAnswer = z.object({
  status: z.number().int(),
  /** The response was a stored one, replayed for a repeated interaction. */
  replayed: z.boolean(),
  body: z.unknown(),
});
export type InvokeAnswer = z.infer<typeof InvokeAnswer>;
